import {
  Box,
  Divider,
  Stack,
  Typography,
  Card,
  Rating,
  CardMedia,
  useMediaQuery,
} from '@mui/material'
import { Link } from '@assets-components'
import { Image, Video, type ImageData, type VideoData } from '@media-components'
import {
  Chip,
  ChipData,
  SectionHeader,
  SectionWrapper,
  type SectionHeaderData,
} from '@components/common'
import { commonStyles, CustomStyles, getStyles } from '@utils/styles'
import { InfiniteSlideImage } from '../InfiniteSlideImage'
import { TestimonialCardProps } from '../ReviewsSection/PeopleCard'
import defaultStyles, { StyleClassNames } from './styles'

export type ClientPortfolioCardData = {
  rating: number
  description: string
  clientData: SectionHeaderData
  imageData?: ImageData
  videoData?: VideoData
  chipData?: ChipData
  linkedinUrl?: string
  categories?: string[]
}

export type ClientPortfolioSectionData = Partial<{
  sectionHeaderData: SectionHeaderData
  featuredRating: ClientPortfolioCardData
  videoData?: VideoData[]
  clientPortfolioCard: TestimonialCardProps[]
  darkBg?: boolean
  featureRatingImage?: ImageData
  featureRatingData?: {
    description?: string
    ratingImage?: ImageData
  }
  featureRatingCenterAligned?: boolean
}>
export type ClientPortfolioProps = ClientPortfolioSectionData & {
  fullMode?: boolean
  alternateBg?: boolean
  csx?: CustomStyles<StyleClassNames>
}

export function ClientPortFolioSection({
  featuredRating,
  clientPortfolioCard,
  sectionHeaderData,
  videoData,
  fullMode,
  alternateBg = true,
  csx,
}: ClientPortfolioProps) {
  const styles = getStyles(defaultStyles, csx)

  const isMobile = useMediaQuery('(max-width: 600px)')

  const videosData = isMobile ? videoData?.slice(0, 1) : videoData

  const cardComps = clientPortfolioCard?.map(
    (
      { clientData, chipData, imageData, videoData, description, rating },
      index
    ) => (
      <Card key={index} sx={styles('card').sx}>
        {fullMode && (
          <CardMedia sx={{ position: 'relative' }}>
            {chipData && (
              <Chip
                {...chipData}
                csx={{
                  notSelected: {
                    padding: '4px 12px',
                    zIndex: 10,
                    height: '28px',
                    top: '18px',
                    left: '16px',
                    color: 'text.primaryDark',
                    bgcolor: '#FFFFFF0D',
                    backdropFilter: 'blur(20px)',
                    boxShadow: '0px 4px 15px 0px #00000026',
                    outlineColor: '#FFFFFF26',
                    position: videoData || imageData ? 'absolute' : 'static',
                  },
                }}
              />
            )}
            {!!videoData && (
              <Box sx={styles('videoWrapper').sx}>
                <Video
                  {...videoData}
                  key={videoData.src}
                  autoPlay
                  loop
                  unmute
                  csx={{
                    wrapper: styles('videoInnerWrapper').sx,
                    video: styles('video').sx,
                  }}
                />
              </Box>
            )}
            {!!imageData && !videoData && (
              <Box sx={styles('videoWrapper').sx}>
                <Image
                  {...imageData}
                  key={imageData.src}
                  sx={styles('image').sx}
                  // csx={{
                  //   wrapper: styles('videoInnerWrapper').sx,
                  //   // video: styles('video').sx,
                  // }}
                />
              </Box>
            )}
          </CardMedia>
        )}
        <Stack spacing={2.5}>
          <Box>
            <Rating defaultValue={rating} readOnly />
          </Box>

          <Typography
            variant="body1.500"
            color="text.primaryDark"
            sx={{
              textWrap: 'initial',
              ...commonStyles.getLineClampStyles(3, 72),
            }}
          >
            {description}
          </Typography>

          <Divider
            sx={{
              backgroundColor: 'custom.lightGrey',
            }}
          />

          <Box sx={{ marginTop: '17px !important' }}>
            <Typography variant="h6.500" color="text.primaryDark">
              {clientData.heading}
            </Typography>
            <Typography
              variant="body2.600"
              color="text.secondaryDark"
              component="div"
            >
              {clientData.description}
            </Typography>
          </Box>
        </Stack>
      </Card>
    )
  )
  return (
    <SectionWrapper alternateBg={alternateBg}>
      <Stack spacing={4}>
        {sectionHeaderData && (
          <SectionHeader
            {...sectionHeaderData}
            csx={{
              wrapper: styles('sectionWrapper').sx,
              heading: styles('sectionHeader').sx,
              description: styles('sectionHeader').sx,
            }}
            darkBg
          />
        )}

        {featuredRating && (
          <Box sx={styles('ratingWrapper').sx}>
            <Box sx={styles('leftRatingWrapper').sx}>
              <Box sx={{ height: '24px', marginLeft: '-12px' }}>
                <Rating defaultValue={featuredRating.rating} readOnly />
              </Box>
              <Box sx={styles('ratingCompanyWrapper').sx}>
                <Typography
                  variant="body1.700"
                  color="text.primaryDark"
                  sx={{ marginRight: '8px', whiteSpace: 'pre' }}
                >
                  {featuredRating.rating}/5
                  <Typography variant="body1.500" sx={{ whiteSpace: 'pre' }}>
                    {' '}
                    rating on
                  </Typography>
                </Typography>
                {featuredRating.imageData && (
                  <Link
                    href="https://clutch.co/profile/bigohtech"
                    target="_blank"
                  >
                    <Image
                      {...featuredRating.imageData}
                      fullWidth
                      sx={styles('ratingFeatured').sx}
                    />
                  </Link>
                )}
              </Box>
            </Box>

            <Divider orientation="vertical" sx={styles('divider').sx} />

            <Typography
              variant="body3"
              color="text.secondaryDark"
              sx={styles('ratingDescription').sx}
            >
              {featuredRating.description}
            </Typography>
          </Box>
        )}

        {!!videosData?.length && (
          <Box sx={styles('videoWrapper').sx}>
            {videosData.map((video) => (
              <Video
                {...video}
                key={video.src}
                autoPlay
                loop
                unmute
                csx={{
                  wrapper: styles('videoInnerWrapper').sx,
                  video: styles('video').sx,
                }}
              />
            ))}
          </Box>
        )}

        <Box sx={styles('cardWrapper').sx}>
          {fullMode ? (
            cardComps
          ) : (
            <InfiniteSlideImage disablePadding>{cardComps}</InfiniteSlideImage>
          )}
        </Box>
      </Stack>
    </SectionWrapper>
  )
}
